"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Api_1 = __importDefault(require("./Api"));
/**
 * Handling all the Podio APIs related to Podio items
 */
class Item extends Api_1.default {
    /**
     * @constructor
     */
    constructor(props) {
        super(props);
    }
    /**
     * Podio item create API
     *
     * @see https://developers.podio.com/doc/items/add-new-item-22362
     * @param {number} app_id - Podio application id
     * @param {SimpleObject} attributes - Object with the attributes data
     * @param {boolean} silent - Should send notification (optional, default: false)
     * @param {boolean} hook - Should trigger hooks (optional, default: true)
     * @returns {Promise<HttpResponse>}
     */
    create(app_id, attributes, silent = false, hook = true) {
        const requestObj = {
            method: 'post',
            url: `/item/app/${app_id}/?silent=${silent}&hook=${hook}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Deletes an item and removes it from all views. The item can no longer be retrieved.
     * @see https://developers.podio.com/doc/items/bulk-delete-items-19406111
     * @param {number} item_id - Podio item id
     * @param {any} attributes - Object with the attributes data (optional)
     * @param {boolean} silent - Should send notification (optional, default: false)
     * @param {boolean} hook - Should trigger hooks (optional, default: true)
     * @returns {Promise<HttpResponse<string>>}
     */
    delete(item_id, attributes = {}, silent = false, hook = true) {
        const requestObj = {
            method: 'delete',
            url: `/item/${item_id}/?silent=${silent}&hook=${hook}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Deletes items from a given app based in bulk and removes them from all views. The data can no longer be retrieved.
     * @param app_id
     * @param attributes
     * @param silent
     * @param hook
     * @returns
     */
    bulkDelete(app_id, attributes = {}, silent = false, hook = true) {
        const requestObj = {
            method: 'post',
            url: `/item/app/${app_id}/delete?silent=${silent}&hook=${hook}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Performs a calculation on the given app
     * @param app_id
     * @param attributes
     * @returns
     */
    calculate(app_id, attributes) {
        const requestObj = {
            method: 'post',
            url: `/item/app/${app_id}/calculate`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Clones the given item creating a new item with identical values in the same app.
     * @param item_id
     * @param silent
     * @param hook
     * @returns
     */
    clone(item_id, silent = false, hook = true) {
        const requestObj = {
            method: 'post',
            url: `/item/${item_id}/clone?silent=${silent}&hook=${hook}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Removes the reference from the item if any
     * @param item_id
     * @returns
     */
    deleteReference(item_id) {
        const requestObj = {
            method: 'delete',
            url: `/item/${item_id}/ref`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Creates a batch for exporting the items. Valid exporters are currently "xls" and "xlsx".
     * @param app_id
     * @param exporter
     * @param attributes
     * @returns
     */
    export(app_id, exporter, attributes = {}) {
        const requestObj = {
            method: 'post',
            url: `/item/app/${app_id}/export/${exporter}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Filters the items and returns the matching items
     * @param app_id
     * @param attributes
     * @returns
     */
    filter(app_id, attributes = {}) {
        const requestObj = {
            method: 'post',
            url: `/item/app/${app_id}/filter/`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Retrieves the items in the app based on the given view
     * @param app_id
     * @param view_id
     * @param attributes
     * @returns
     */
    filterByView(app_id, view_id, attributes = {}) {
        const requestObj = {
            method: 'post',
            url: `/item/app/${app_id}/filter/${view_id}/`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Used to find possible items for a given application field. It searches the relevant apps for items matching the given text.
     * @param field_id
     * @param attributes
     * @returns
     */
    searchField(field_id, attributes = {}) {
        const requestObj = {
            method: 'get',
            url: `/item/field/${field_id}/find`,
            params: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the range for the given field. Only valid for fields of type "number", "calculation" and "money".
     * @param field_id
     * @returns
     */
    getFieldRange(field_id) {
        const requestObj = {
            method: 'get',
            url: `/item/field/${field_id}/range`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Podio get item API
     *
     * @see https://developers.podio.com/doc/items/get-item-22360
     * @param {number} item_id - Podio item id
     * @returns {Promise<HttpResponse>}
     */
    get(item_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the full item by its app_item_id, which is a unique ID for items per app.
     * @param app_id
     * @param app_item_id
     * @returns
     */
    getByAppItemId(app_id, app_item_id) {
        const requestObj = {
            method: 'get',
            url: `/app/${app_id}/item/${app_item_id}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Retrieve an app item with the given external_id.
     * @param app_id
     * @param external_id
     * @returns
     */
    getByExternalId(app_id, external_id) {
        const requestObj = {
            method: 'get',
            url: `/item/app/${app_id}/external_id/${external_id}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Return all the values cloned for an item. The values are not saved, but can be used as values for creating a new item.
     * @param item_id
     * @returns
     */
    getClone(item_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/clone`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the number of items on app matching a given saved view or set of filter(s).
     * @param app_id
     * @returns
     */
    getCount(app_id) {
        const requestObj = {
            method: 'get',
            url: `/item/app/${app_id}/count`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Return the values for the given field on the given item. The field can be given either as the id of the field or the external id of the field.
     * @param item_id
     * @param field_or_external_id
     * @returns
     */
    getFieldValue(item_id, field_or_external_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/value/${field_or_external_id}/v2`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns a preview of the item for referencing on the given field.
     * @param item_id
     * @param field_id
     * @returns
     */
    getBasicByField(item_id, field_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/reference/${field_id}/preview`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns up to thousand items that have a reference to the given item. The references are grouped by app. Both the apps and the items are sorted by title.
     * @param item_id
     * @returns
     */
    getReference(item_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/reference/`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the data about the specific revision on an item
     * @param item_id
     * @param revision
     * @returns
     */
    getRevision(item_id, revision) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/revision/${revision}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the difference in fields values between the two revisions.
     * @param item_id
     * @param revision_from
     * @param revision_to
     * @returns
     */
    getRevisionDifference(item_id, revision_from, revision_to) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/revision/${revision_from}/${revision_to}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns all the revisions that have been made to an item (up to a maximum of the last 30 revisions).
     * @param item_id
     * @returns
     */
    getRevisions(item_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/revision/`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns all the values for an item, with the additional data provided by the get item operation.
     * @param item_id
     * @returns
     */
    getValues(item_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/value/v2`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the items in the Xlsx format.
     * @param app_id
     * @returns
     */
    xlsx(app_id) {
        const requestObj = {
            method: 'get',
            url: `/item/app/${app_id}/xlsx/`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Gets the URL to join the given meeting. If the user is the organizer of the meeting, the meeting will be started and the URL will log in the user automatically.
     * @param item_id
     * @returns
     */
    getMeetingUrl(item_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/meeting/url`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns all the references to the item from the given field
     * @param item_id
     * @param field_id
     * @returns
     */
    getReferenceByField(item_id, field_id) {
        const requestObj = {
            method: 'get',
            url: `/item/${item_id}/reference/field/${field_id}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Returns the top possible values for the given field. This is currently only valid for fields of type "app".
     * @param field_id
     * @returns
     */
    getTopValuesByField(field_id, attributes = {}) {
        const requestObj = {
            method: 'get',
            url: `/item/field/${field_id}/top/`,
            params: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Update the item's values
     * and/or
     * its (internal) priority.
     * Mostly intended for moving the item card in the card view.
     * @param item_id
     * @param attributes
     * @returns
     */
    rearrange(item_id, attributes = {}) {
        const requestObj = {
            method: 'post',
            url: `/item/${item_id}/rearrange`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Reverts the change done in the given revision.
     * @param item_id
     * @param revision
     * @returns
     */
    revert(item_id, revision) {
        const requestObj = {
            method: 'delete',
            url: `/item/${item_id}/revision/${revision}`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Reverts the item to the values in the given revision. This will undo any changes made after the given revision.
     * @param item_id
     * @param revision
     * @returns
     */
    revertTo(item_id, revision) {
        const requestObj = {
            method: 'post',
            url: `/item/${item_id}/revision/${revision}/revert_to`,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Updates the participation status for the active user on the item
     * @param item_id
     * @param attributes
     * @returns
     */
    participation(item_id, attributes = {}) {
        const requestObj = {
            method: 'put',
            url: `/item/${item_id}/participation`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Update an already existing item
     * @param item_id - Podio item id
     * @param attributes - Object with the attributes data
     * @param silent - Should send notification (optional, default: false)
     * @param hook - Should trigger hooks (optional, default: true)
     * @returns
     */
    update(item_id, attributes, silent = false, hook = true) {
        const requestObj = {
            method: 'put',
            url: `/item/${item_id}/?silent=${silent}&hook=${hook}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Update the item values for a specific field. The identifier for the field can either be the field_id or the external_id for the field.
     * @param item_id
     * @param field_id
     * @param attributes
     * @param silent
     * @param hook
     * @returns
     */
    updateField(item_id, field_id, attributes, silent = false, hook = true) {
        const requestObj = {
            method: 'put',
            url: `/item/${item_id}/value/${field_id}?silent=${silent}&hook=${hook}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
    /**
     * Updates all the values for an item
     * @param item_id
     * @param attributes
     * @param silent
     * @param hook
     * @returns
     */
    updateValues(item_id, attributes, silent = false, hook = true) {
        const requestObj = {
            method: 'put',
            url: `/item/${item_id}/value?silent=${silent}&hook=${hook}`,
            data: attributes,
        };
        return this._httpRequest(requestObj);
    }
}
exports.default = Item;
